import React, { useCallback } from "react";
import { Mail, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";
import { BrandBar } from "@/components/landing/HeaderBrand";

const FooterBrand = () => (
  <div className="lg:col-span-5">
    <img
      src={COMPANY.logoLight}
      alt={`${COMPANY.name} logo`}
      className="h-[7.5rem] w-auto object-contain sm:h-[8.75rem]"
      loading="lazy"
    />
    <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-300">
      A Dubai-based group company developing online travel platforms across visa
      applications, travel insurance and travel eSIMs.
    </p>
  </div>
);

const FooterNav = ({ onSelect }) => (
  <div className="lg:col-span-3">
    <h3 className="text-sm font-semibold text-white">Explore</h3>
    <ul className="mt-4 space-y-2.5">
      {NAV_LINKS.map((link) => (
        <li key={link.id}>
          <button
            type="button"
            data-testid={`footer-nav-${link.id}-link`}
            onClick={() => onSelect(link.id)}
            className="text-sm text-slate-300 transition-colors hover:text-[#7fd6d9]"
          >
            {link.label}
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const FooterContact = () => (
  <div className="lg:col-span-4">
    <h3 className="text-sm font-semibold text-white">Head office</h3>
    <ul className="mt-4 space-y-3.5">
      <li className="flex items-start gap-3">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#f28a2e]" aria-hidden="true" />
        <span className="text-sm text-slate-300">
          {COMPANY.address.line1}
          <br />
          {COMPANY.address.city} - {COMPANY.address.country}
        </span>
      </li>
      <li className="flex items-center gap-3">
        <MessageCircle className="h-4 w-4 shrink-0 text-[#1aa3a8]" aria-hidden="true" />
        <a
          href={COMPANY.whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-whatsapp-link"
          className="text-sm text-slate-300 transition-colors hover:text-[#7fd6d9]"
        >
          {COMPANY.whatsappDisplay}
        </a>
      </li>
      <li className="flex items-center gap-3">
        <Mail className="h-4 w-4 shrink-0 text-[#1aa3a8]" aria-hidden="true" />
        <a
          href={COMPANY.emailLink}
          data-testid="footer-email-link"
          className="text-sm text-slate-300 transition-colors hover:text-[#7fd6d9]"
        >
          {COMPANY.email}
        </a>
      </li>
    </ul>
    <ul className="mt-5 flex flex-wrap gap-2">
      {SERVICES.map((service) => (
        <li
          key={service.key}
          className="rounded-full border border-white/20 px-2.5 py-1 text-[11px] font-medium text-slate-200"
        >
          {service.title}
        </li>
      ))}
    </ul>
  </div>
);

const FooterLegal = () => (
  <div className="mt-10 flex flex-col gap-3 border-t border-white/15 pt-6 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
    <p>
      © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
    </p>
    <p>Registered in the Dubai - United Arab Emirates</p>
  </div>
);

export const Footer = () => {
  const handleSelect = useCallback((id) => scrollToId(id), []);

  return (
    <footer data-testid="site-footer" className="bg-[#0b1d3a] text-slate-300">
      <BrandBar order="teal" />
      <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12">
          <FooterBrand />
          <FooterNav onSelect={handleSelect} />
          <FooterContact />
        </div>
        <FooterLegal />
      </div>
    </footer>
  );
};
